<template>
  <TableCustomMini
    ref="ticketsTable"
    class="table-custom-mini"
    :fields="fields"
    :hasArchiveButton="store.getters.hasErrandEditorAccess"
    :hasCreateButton="store.getters.hasErrandCreatorAccess"
    :hasDeleteButton="store.getters.hasErrandAdminAccess"
    :hasEditButton="store.getters.hasErrandEditorAccess"
    :hasMenu="store.getters.hasErrandEditorAccess || store.getters.hasErrandAdminAccess"
    :hasSorting="true"
    :items="ticketItems"
    :itemType="t('general__label__tickets')"
    :selectedId="selectedTicketId"
    :wideSecondColumn="true"
    @archiveItem="emits('archiveTicket')"
    @deleteItem="emits('deleteTicket')"
    @editItem="openTicket(selectedTicketId)"
    @loadMore="emits('loadMoreTickets')"
    @menuClosed="piniaStore.setSelectedTicket(null)"
    @menuOpened="selectTicket($event)"
  >
    <template #preview-heading>
      <span style="width: 48px"></span>
    </template>
    <template #preview="{ item }">
      <ButtonText
        class="table-custom-mini__button"
        :class="{ selected: item.id === selectedTicketId }"
        :iconLeft="iconArrowUpRight"
        size="extra-small"
        :whiteContent="false"
        @onClicked="openTicket(item.id)"
      ></ButtonText>
    </template>
  </TableCustomMini>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import TableCustomMini, { TableItem } from '@/design-system/molecules/TableCustomMini.vue'
import { translatedPriority, translatedStatus } from '@/utils/ticketHelpers'
import { Ticket } from '@/services/tickets.service'
import iconArrowUpRight from '@/design-system/assets/icons/Icon-Arrow-up-right.svg'

type Props = {
  fields: any[]
  items?: Ticket[]
  tabOption: string
}
const props = defineProps<Props>()
type Emits = {
  (e: 'archiveTicket'): void
  (e: 'deleteTicket'): void
  (e: 'loadMoreTickets'): void
}
const emits = defineEmits<Emits>()
const { t } = useI18n()

const piniaStore = useGeneralStore()
const store = useStore()
const ticketsTable = ref()
const ticketItems = computed<TableItem[]>(() => {
  if (!props.items) return []
  return props.items.map((item) => {
    return {
      id: item.id,
      name: item.title,
      sorting: getSorting(item),
      active: item.active
    }
  })
})
const selectedTicketId = computed(() => {
  return piniaStore.selectedTicket?.id ?? ''
})
const showOverlayModal = computed(() => {
  return store.state.showOverlayModal
})

function getSorting(item: Ticket) {
  switch (props.tabOption) {
    case 'status':
      return translatedStatus(item.status)
    case 'priority':
      return translatedPriority(item.priority)
    case 'dueDate':
      return item.dueDate ? item.dueDate.substring(2, 10) : ''
    default:
      return ''
  }
}
function openTicket(ticketId: string) {
  ticketsTable.value.closeMenu()
  if (!ticketId?.length) return piniaStore.setSelectedLink(null)
  selectTicket(ticketId)
  piniaStore.setCurrentOverlayModalPinia('editTicket')
  piniaStore.setShowOverlayModalPinia(true)
}
async function selectTicket(ticketId: string) {
  const selectedTicket = piniaStore.ticketsList.find((t) => t.id === ticketId)
  if (selectedTicket) piniaStore.setSelectedTicket(selectedTicket)
}
watch(showOverlayModal, () => {
  if (!showOverlayModal.value) {
    piniaStore.setSelectedTicket(null)
  }
})
watch(
  () => piniaStore.selectedTicket,
  () => {
    if (!piniaStore.selectedTicket) {
      ticketsTable.value.closeMenu()
    }
  }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.table-custom-mini {
  flex: 1 1 auto;
  min-height: calc((32px * 6) + 16px); // 6 rows + 16px padding
  padding: 16px variables.$padding-main-body;
  padding-top: 0px;
  z-index: 1;
  &__button {
    padding: 0;
    &.selected {
      background-color: variables.$color-primary;
      :deep(img) {
        filter: brightness(100);
      }
    }
    &:hover:not(.selected) {
      background-color: rgba(205, 222, 254, 0.2);
    }
  }
}
</style>
